import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import './AccessVerification.css';

// Language translations
const translations = {
    en: {
        title: "Members Only",
        description: "This exclusive page is available for customers who have purchased",
        verifyText: "Please verify your access below.",
        emailLabel: "Enter your email address",
        emailPlaceholder: "your-email@example.com",
        verifyButton: "Verify Access",
        verifying: "Verifying...",
        helpText: "Enter the email address you used when purchasing the product",
        accessGranted: "Access Granted!",
        accessGrantedDesc: "Welcome back! Redirecting to the page...",
        accessDenied: "Access Denied",
        accessDeniedDesc: "We couldn't find an active purchase for \"{productName}\" with this email address. Please make sure you're using the same email you used when purchasing.",
        tryAgain: "Try Again",
        contactSupport: "Contact Support",
        verificationError: "Verification Error",
        errorDesc: "Something went wrong. Please try again.",
        securityBadge: "Secure Access Verification"
    },
    de: {
        title: "Nur für Mitglieder",
        description: "Diese exklusive Seite ist verfügbar für Kunden, die",
        verifyText: "Bitte bestätigen Sie Ihren Zugang unten.",
        emailLabel: "Geben Sie Ihre E-Mail-Adresse ein",
        emailPlaceholder: "ihre-email@example.com",
        verifyButton: "Zugang bestätigen",
        verifying: "Wird überprüft...",
        helpText: "Geben Sie die E-Mail-Adresse ein, die Sie beim Kauf verwendet haben",
        accessGranted: "Zugang gewährt!",
        accessGrantedDesc: "Willkommen zurück! Weiterleitung zur Seite...",
        accessDenied: "Zugang verweigert",
        accessDeniedDesc: "Wir konnten keinen aktiven Kauf für \"{productName}\" mit dieser E-Mail-Adresse finden. Bitte stellen Sie sicher, dass Sie die gleiche E-Mail verwenden, die Sie beim Kauf verwendet haben.",
        tryAgain: "Erneut versuchen",
        contactSupport: "Support kontaktieren",
        verificationError: "Überprüfungsfehler",
        errorDesc: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
        securityBadge: "Sichere Zugangsüberprüfung"
    }
};

function AccessVerification({ 
    pageData, 
    onAccessGranted, 
    onAccessDenied 
}) {
    const [email, setEmail] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState('initial'); // 'initial', 'success', 'failed', 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [productName, setProductName] = useState('');
    const [language, setLanguage] = useState('en');

    useEffect(() => {
        // Set product name and language from pageData
        if (pageData?.accessControlProductName) {
            setProductName(pageData.accessControlProductName);
        }
        if (pageData?.accessControlLanguage) {
            setLanguage(pageData.accessControlLanguage);
            console.log('🌍 AccessVerification: Language set to:', pageData.accessControlLanguage);
        } else {
            console.log('🌍 AccessVerification: No language set, defaulting to English');
        }
        
        console.log('📊 AccessVerification: PageData received:', {
            productName: pageData?.accessControlProductName,
            language: pageData?.accessControlLanguage,
            isOnlyForMembers: pageData?.isOnlyForMembers
        });
    }, [pageData]);

    // Get current translations
    const t = translations[language] || translations.en;

    const verifyAccess = async () => {
        if (!email.trim()) {
            setErrorMessage(language === 'de' ? 'Bitte geben Sie Ihre E-Mail-Adresse ein' : 'Please enter your email address');
            return;
        }

        if (!pageData?.accessControlProductId) {
            setErrorMessage(language === 'de' ? 'Zugriffskontrolle nicht richtig konfiguriert' : 'Access control not properly configured');
            return;
        }

        setIsVerifying(true);
        setErrorMessage('');

        try {
            console.log('🔍 AccessVerification: Product ID:', pageData.accessControlProductId);

            // Verified server-side: the purchase records stay on the backend and
            // only a boolean comes back, so no visitor can read customer data.
            const verify = httpsCallable(functions, 'verifyProductAccess');
            const result = await verify({
                productId: pageData.accessControlProductId,
                email: email.trim(),
            });

            const hasAccess = result?.data?.hasAccess === true;
            console.log('🔍 AccessVerification: Access granted:', hasAccess);

            if (hasAccess) {
                setVerificationStatus('success');
                setTimeout(() => {
                    onAccessGranted();
                }, 1500); // Show success message briefly before granting access
            } else {
                setVerificationStatus('failed');
            }

        } catch (error) {
            console.error('Error verifying access:', error);
            setVerificationStatus('error');
            setErrorMessage(language === 'de' ? 'Überprüfung fehlgeschlagen. Bitte versuchen Sie es erneut.' : 'Verification failed. Please try again.');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isVerifying) {
            verifyAccess();
        }
    };

    const handleRetry = () => {
        setVerificationStatus('initial');
        setErrorMessage('');
        setEmail('');
    };

    const renderVerificationContent = () => {
        switch (verificationStatus) {
            case 'success':
                return (
                    <div className="verification-success">
                        <div className="success-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <h3>{t.accessGranted}</h3>
                        <p>{t.accessGrantedDesc}</p>
                        <div className="success-spinner"></div>
                    </div>
                );

            case 'failed':
                return (
                    <div className="verification-failed">
                        <div className="failed-icon">
                            <i className="fas fa-times-circle"></i>
                        </div>
                        <h3>{t.accessDenied}</h3>
                        <p>
                            {t.accessDeniedDesc.replace('{productName}', productName)}
                        </p>
                        <div className="failed-actions">
                            <button 
                                className="retry-btn"
                                onClick={handleRetry}
                            >
                                <i className="fas fa-redo"></i>
                                {t.tryAgain}
                            </button>
                            <button 
                                className="contact-btn"
                                onClick={() => window.open('mailto:support@bonusclub.io?subject=Access Issue', '_blank')}
                            >
                                <i className="fas fa-envelope"></i>
                                {t.contactSupport}
                            </button>
                        </div>
                    </div>
                );

            case 'error':
                return (
                    <div className="verification-error">
                        <div className="error-icon">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <h3>{t.verificationError}</h3>
                        <p>{errorMessage || t.errorDesc}</p>
                        <button 
                            className="retry-btn"
                            onClick={handleRetry}
                        >
                            <i className="fas fa-redo"></i>
                            {t.tryAgain}
                        </button>
                    </div>
                );

            default:
                return (
                    <div className="verification-form">
                        <div className="form-header">
                            <div className="lock-icon-2">
                                <i className="fas fa-key"></i>
                            </div>
                            <h3>{t.title}</h3>
                            <p>
                                {t.description} <strong>"{productName}"</strong>. {t.verifyText}
                            </p>
                        </div>

                        <div className="email-input-section">
                            <label htmlFor="verification-email" className="input-label">
                                {t.emailLabel}
                            </label>
                            <div className="input-wrapper">
                                <input
                                    id="verification-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder={t.emailPlaceholder}
                                    className="email-input"
                                    disabled={isVerifying}
                                />
                                <div className="input-icon">
                                    <i className="fas fa-envelope"></i>
                                </div>
                            </div>
                            {errorMessage && (
                                <div className="error-message">
                                    <i className="fas fa-exclamation-circle"></i>
                                    {errorMessage}
                                </div>
                            )}
                        </div>

                        <button 
                            className="verify-btn"
                            onClick={verifyAccess}
                            disabled={isVerifying || !email.trim()}
                        >
                            {isVerifying ? (
                                <>
                                    <div className="btn-spinner"></div>
                                    {t.verifying}
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-key"></i>
                                    {t.verifyButton}
                                </>
                            )}
                        </button>

                        <div className="help-text">
                            <p>
                                <i className="fas fa-heart"></i>
                                {t.helpText}
                            </p>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="access-verification-overlay">
            {/* Additional Floating Orbs */}
            <div className="floating-orb orb-1"></div>
            <div className="floating-orb orb-2"></div>
            <div className="floating-orb orb-3"></div>
            <div className="floating-orb orb-4"></div>
            
            <div className="access-verification-container">
                <div className="verification-content">
                    {renderVerificationContent()}
                </div>
                
                <div className="verification-footer">
                    <div className="security-badge">
                        <i className="fas fa-shield-alt"></i>
                        <span>{t.securityBadge}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AccessVerification;
